import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation, languages } from '../lib/i18n';
import { authAPI, accountsAPI, recurringAPI, budgetsAPI, categoriesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import {
  Clover, ChevronRight, ChevronLeft, Check,
  Wallet, Globe, DollarSign, Briefcase, ShoppingCart,
  Sparkles, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const currencies = [
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
];

const OnboardingWizard = ({ onComplete }) => {
  const { user, updateUser, language } = useAuth();
  const t = useTranslation(language);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);

  // Step 1: Preferences
  const [selectedCurrency, setSelectedCurrency] = useState(user?.currency || 'EUR');
  const [selectedLanguage, setSelectedLanguage] = useState(user?.language || 'es');

  // Step 2: Account
  const [accountName, setAccountName] = useState('Cuenta Principal');
  const [accountBalance, setAccountBalance] = useState('');
  const [createSecondAccount, setCreateSecondAccount] = useState(false);
  const [secondAccountName, setSecondAccountName] = useState('Efectivo');

  // Step 3: Income
  const [incomeName, setIncomeName] = useState('Salario');
  const [incomeAmount, setIncomeAmount] = useState('');
  const [incomeDay, setIncomeDay] = useState('1');

  // Step 4: Budget
  const [budgetAmount, setBudgetAmount] = useState('');
  const [selectedBudgetCategory, setSelectedBudgetCategory] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [accountsRes, categoriesRes] = await Promise.all([
        accountsAPI.getAll(),
        categoriesAPI.getAll()
      ]);
      setAccounts(accountsRes.data);
      setCategories(categoriesRes.data);

      // Set default account name if exists
      if (accountsRes.data.length > 0) {
        setAccountName(accountsRes.data[0].name);
      }

      // Find "Alimentación" category for default budget
      const foodCat = categoriesRes.data.find(c =>
        c.name.toLowerCase().includes('alimentación') || c.name.toLowerCase().includes('food')
      );
      if (foodCat) {
        setSelectedBudgetCategory(foodCat.id);
      }
    } catch (error) {
      console.error('Error loading data');
    }
  };

  const handleStep1 = async () => {
    setLoading(true);
    try {
      await authAPI.updateProfile({
        currency: selectedCurrency,
        language: selectedLanguage
      });
      updateUser({ currency: selectedCurrency, language: selectedLanguage });
      setStep(2);
    } catch (error) {
      toast.error('Error al guardar preferencias');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async () => {
    setLoading(true);
    try {
      // Update existing account
      if (accounts.length > 0) {
        await accountsAPI.update(accounts[0].id, {
          name: accountName,
          balance: parseFloat(accountBalance) || 0
        });
      }

      // Create second account if requested
      if (createSecondAccount && secondAccountName) {
        await accountsAPI.create({
          name: secondAccountName,
          type: 'cash',
          balance: 0,
          currency: selectedCurrency
        });
      }

      setStep(3);
    } catch (error) {
      toast.error('Error al configurar cuentas');
    } finally {
      setLoading(false);
    }
  };

  const handleStep3 = async () => {
    setLoading(true);
    try {
      if (incomeAmount && parseFloat(incomeAmount) > 0) {
        // Find salary category
        const salaryCat = categories.find(c =>
          c.name.toLowerCase().includes('salario') || c.name.toLowerCase().includes('salary')
        );

        // Calculate next date based on selected day
        const today = new Date();
        const nextDate = new Date(today.getFullYear(), today.getMonth(), parseInt(incomeDay));
        if (nextDate <= today) {
          nextDate.setMonth(nextDate.getMonth() + 1);
        }

        // Ensure we have an account to tie the income to
        let targetAccountId = accounts[0]?.id;
        if (!targetAccountId) {
          // If state wasn't updated, try fetching again
          const accRes = await accountsAPI.getAll();
          if (accRes.data && accRes.data.length > 0) {
            targetAccountId = accRes.data[0].id;
          } else {
            throw new Error("No hay cuentas disponibles para este ingreso");
          }
        }

        let targetCategoryId = salaryCat?.id || categories[0]?.id;
        if (!targetCategoryId) {
          const catRes = await categoriesAPI.getAll();
          if (catRes.data && catRes.data.length > 0) {
            targetCategoryId = catRes.data[0].id;
          }
        }

        await recurringAPI.create({
          description: incomeName,
          amount: parseFloat(incomeAmount),
          type: 'income',
          category_id: targetCategoryId || "",
          account_id: targetAccountId || "",
          frequency: 'monthly',
          start_date: nextDate.toISOString(),
          is_active: true
        });
      }
      setStep(4);
    } catch (error) {
      toast.error('Error al crear ingreso recurrente');
    } finally {
      setLoading(false);
    }
  };

  const handleStep4 = async () => {
    setLoading(true);
    try {
      if (budgetAmount && parseFloat(budgetAmount) > 0 && selectedBudgetCategory) {
        await budgetsAPI.create({
          category_id: selectedBudgetCategory,
          amount: parseFloat(budgetAmount),
          period: 'monthly'
        });
      }

      // Mark onboarding as complete
      await authAPI.completeOnboarding();
      updateUser({ onboarding_completed: true });

      toast.success('¡Configuración completada!');
      onComplete();
    } catch (error) {
      toast.error('Error al completar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      await authAPI.completeOnboarding();
      updateUser({ onboarding_completed: true });
      onComplete();
    } catch (error) {
      onComplete();
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[1, 2, 3, 4].map((s) => (
        <div
          key={s}
          className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all",
            s === step
              ? "bg-primary text-primary-foreground scale-110"
              : s < step
                ? "bg-primary/20 text-primary"
                : "bg-muted text-muted-foreground"
          )}
        >
          {s < step ? <Check size={18} /> : s}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center">
            <Clover className="w-7 h-7 text-primary-foreground" strokeWidth={1.5} />
          </div>
          <div>
            <span className="font-display font-bold text-2xl tracking-tight text-foreground">TREBOL</span>
            <span className="text-sm text-muted-foreground block -mt-1">FINANCE</span>
          </div>
        </div>

        {renderStepIndicator()}

        <Card className="border-border rounded-sm shadow-lg">
          <CardContent className="p-6">
            {/* Step 1: Welcome & Preferences */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center">
                  <Sparkles className="w-12 h-12 mx-auto text-primary mb-4" />
                  <h2 className="text-xl font-bold text-foreground mb-2">
                    ¡Bienvenido a TREBOL Finance!
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    Configura tus preferencias para empezar
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <DollarSign size={16} className="text-primary" />
                      Moneda Principal
                    </Label>
                    <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map(c => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.symbol} {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe size={16} className="text-primary" />
                      Idioma
                    </Label>
                    <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {languages.map(l => (
                          <SelectItem key={l.code} value={l.code}>
                            {l.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="ghost" onClick={handleSkip} className="text-muted-foreground">
                    Saltar todo
                  </Button>
                  <Button onClick={handleStep1} disabled={loading} className="bg-primary hover:bg-primary/90 rounded-sm">
                    Continuar <ChevronRight size={18} className="ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Accounts */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center">
                  <Wallet className="w-12 h-12 mx-auto text-primary mb-4" />
                  <h2 className="text-xl font-bold text-foreground mb-2">
                    Configura tus Cuentas
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    Personaliza tu cuenta principal
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nombre de la cuenta</Label>
                    <Input
                      value={accountName}
                      onChange={(e) => setAccountName(e.target.value)}
                      placeholder="Ej: Cuenta Corriente"
                      className="rounded-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Saldo inicial (opcional)</Label>
                    <Input
                      type="number"
                      value={accountBalance}
                      onChange={(e) => setAccountBalance(e.target.value)}
                      placeholder="0.00"
                      className="rounded-sm"
                    />
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-sm">
                    <input
                      type="checkbox"
                      id="secondAccount"
                      checked={createSecondAccount}
                      onChange={(e) => setCreateSecondAccount(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor="secondAccount" className="text-sm text-foreground cursor-pointer">
                      Crear segunda cuenta (Efectivo)
                    </label>
                  </div>

                  {createSecondAccount && (
                    <div className="space-y-2">
                      <Label>Nombre segunda cuenta</Label>
                      <Input
                        value={secondAccountName}
                        onChange={(e) => setSecondAccountName(e.target.value)}
                        placeholder="Ej: Efectivo"
                        className="rounded-sm"
                      />
                    </div>
                  )}
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="ghost" onClick={() => setStep(1)}>
                    <ChevronLeft size={18} className="mr-1" /> Atrás
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={handleSkip} className="text-muted-foreground">
                      Saltar
                    </Button>
                    <Button onClick={handleStep2} disabled={loading} className="bg-primary hover:bg-primary/90 rounded-sm">
                      Continuar <ChevronRight size={18} className="ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Income */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center">
                  <Briefcase className="w-12 h-12 mx-auto text-primary mb-4" />
                  <h2 className="text-xl font-bold text-foreground mb-2">
                    Tu Ingreso Principal
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    Configura tu ingreso mensual recurrente
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Descripción</Label>
                    <Input
                      value={incomeName}
                      onChange={(e) => setIncomeName(e.target.value)}
                      placeholder="Ej: Salario"
                      className="rounded-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Monto mensual</Label>
                    <Input
                      type="number"
                      value={incomeAmount}
                      onChange={(e) => setIncomeAmount(e.target.value)}
                      placeholder="0.00"
                      className="rounded-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Día de cobro</Label>
                    <Select value={incomeDay} onValueChange={setIncomeDay}>
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 5, 10, 15, 20, 25, 28, 30].map(day => (
                          <SelectItem key={day} value={day.toString()}>
                            Día {day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="ghost" onClick={() => setStep(2)}>
                    <ChevronLeft size={18} className="mr-1" /> Atrás
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={handleSkip} className="text-muted-foreground">
                      Saltar
                    </Button>
                    <Button onClick={handleStep3} disabled={loading} className="bg-primary hover:bg-primary/90 rounded-sm">
                      Continuar <ChevronRight size={18} className="ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Budget */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="text-center">
                  <ShoppingCart className="w-12 h-12 mx-auto text-primary mb-4" />
                  <h2 className="text-xl font-bold text-foreground mb-2">
                    Tu Primer Presupuesto
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    Establece un límite para controlar tus gastos
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Categoría</Label>
                    <Select value={selectedBudgetCategory} onValueChange={setSelectedBudgetCategory}>
                      <SelectTrigger className="rounded-sm">
                        <SelectValue placeholder="Selecciona categoría" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.filter(c => !c.name.toLowerCase().includes('salario') && !c.name.toLowerCase().includes('ingreso')).map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Presupuesto mensual</Label>
                    <Input
                      type="number"
                      value={budgetAmount}
                      onChange={(e) => setBudgetAmount(e.target.value)}
                      placeholder="0.00"
                      className="rounded-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Te avisaremos al alcanzar el 80% y 100%
                    </p>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="ghost" onClick={() => setStep(3)}>
                    <ChevronLeft size={18} className="mr-1" /> Atrás
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={handleSkip} className="text-muted-foreground">
                      Saltar
                    </Button>
                    <Button onClick={handleStep4} disabled={loading} className="bg-primary hover:bg-primary/90 rounded-sm">
                      <Check size={18} className="mr-1" /> Finalizar
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-4">
          Puedes modificar todo esto después en Configuración
        </p>
      </div>
    </div>
  );
};

export default OnboardingWizard;
